import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';
import { Params } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

export abstract class NebulaDataSource<T> extends DataSource<T> {

  public static MIN_PAGESIZE = 10;
  public static MAX_PAGESIZE = 50;
  public static DEFAULT_AUTOCOMPLETE_PAGESIZE = 10;
  public static DEFAULT_TIMELINE_PAGESIZE = 20;
  private static _SPECIAL_CHAR_REGEX = new RegExp('/\\|\'/g');

  private itemsSubject = new BehaviorSubject<T[]>([]);
  private nextPageToken: string;
  private currentPageToken: string;
  private pageSize: number;
  protected orderBy: string;
  private filterCriteria: string;
  protected cachedPages = new Map<number, Array<T>>();
  public currentPage = 0;
  public filterableFields: Array<string>;
  public dateFields: Array<string> = [];
  private loadingItems = false;

  public static escapeFilterValue(value: string): string {
    return value.replace(NebulaDataSource._SPECIAL_CHAR_REGEX, '\\$&');
  }

  protected getPageSize(): number {
    return this.pageSize;
  }

  public hasNextPage() {
    return !this.loadingItems && (this.currentPage + 1 < this.cachedPages.size || !!this.nextPageToken);
  }

  public hasPreviousPage() {
    return !this.loadingItems && this.currentPage > 0;
  }

  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    if (this.itemsSubject.isStopped) {
      this.itemsSubject = new BehaviorSubject<T[]>([]);
    }
    return this.itemsSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject.complete();
  }

  public firstPage(filterCriteria: string, orderBy?: string): Observable<GetPageFunctionResponse<T>> {
    return this.loadPage(filterCriteria, 0, orderBy || this.orderBy);
  }

  public nextPage(): Observable<GetPageFunctionResponse<T>> {
    return this.loadPage(this.filterCriteria, this.currentPage + 1, this.orderBy);
  }

  public previousPage(): Observable<GetPageFunctionResponse<T>> {
    return this.loadPage(this.filterCriteria, this.currentPage - 1, this.orderBy);
  }

  public applyFilter(filterCriteria: string): Observable<GetPageFunctionResponse<T>> {
    if (this.filterCriteria !== filterCriteria) {
      return this.firstPage(filterCriteria)
        .pipe(
          tap(() => {
            this.filterCriteria = filterCriteria;
          })
        );
    } else {
      const pageData = this.cachedPages.get(0) ?? [];
      return of({
        pageElements: pageData,
        nextPageToken: undefined
      });
    }
  }

  public refreshPage(): Observable<GetPageFunctionResponse<T>> {
    let pageData: T[] = [];
    return this.loadPageFunction(
      this.filterCriteria,
      this.getPageSize(),
      this.currentPageToken,
      this.orderBy
    )
      .pipe(
        tap((response: GetPageFunctionResponse<T>) => {
          pageData = response.pageElements;
          this.nextPageToken = response.nextPageToken;
          this.cachedPages.set(this.currentPage, pageData);
          this.itemsSubject.next(this.cachedPages.get(this.currentPage));
          this.updatePaginator(this.currentPage);
        })
      );
  }

  public getFilterCriteria(queryParams: Params): string {
    let filterCriteria = '';
    for (const key in queryParams) {
      if (this.filterableFields.indexOf(key) !== -1) {
        if (filterCriteria) {
          filterCriteria += ' && ';
        }
        const value = queryParams[key];
        if (this.dateFields.indexOf(key) !== -1) {
          const end = new Date(Array.isArray(value) ? value[1] : value);
          end.setTime(end.getTime() + 24 * 60 * 60 * 1000);
          filterCriteria += key + ' >= timestamp(\'' + new Date(Array.isArray(value) ? value[0] : value).toISOString() + '\') && '
            + key + ' < timestamp(\'' + end.toISOString() + '\')';
        } else {
          filterCriteria += key + '.contains(\'' + NebulaDataSource.escapeFilterValue(value) + '\')';
        }
      }
    }
    return filterCriteria;
  }

  private loadPage(filterCriteria: string, page: number, orderBy: string): Observable<GetPageFunctionResponse<T>> {
    let pageData: T[] = [];

    const previousPageIndex = this.currentPage;

    this.loadingItems = true;

    if (page > 0 && this.cachedPages.get(page)?.length > 0) {
      pageData = this.cachedPages.get(page);
      this.itemsSubject.next(this.cachedPages.get(page));
      this.currentPage = page;
      this.loadingItems = false;
      this.updatePaginator(previousPageIndex);
      return of({
        pageElements: pageData,
        nextPageToken: undefined
      });
    } else {
      return this.loadPageFunction(
        filterCriteria,
        this.getPageSize(),
        // Cache is cleaned after first page call is correct
        // So the nextPageToken input MUST be undefined when
        // calling the first page
        page > 0 ? this.nextPageToken : undefined,
        orderBy
      )
        .pipe(
          catchError((error) => {
            this.loadingItems = false;
            this.updatePaginator(this.currentPage);
            return throwError(error);
          }),
          tap(() => {
            if (page === 0) {
              this.emptyCache();
            }
          }),
          tap((response: GetPageFunctionResponse<T>) => {
            pageData = response.pageElements;
            this.filterCriteria = filterCriteria;
            this.orderBy = orderBy;
            this.currentPage = page;
            this.currentPageToken = this.nextPageToken;
            this.nextPageToken = response.nextPageToken || undefined;
            this.cachedPages.set(page, pageData);
            this.itemsSubject.next(this.cachedPages.get(page));
            this.loadingItems = false;
            this.updatePaginator(previousPageIndex);
          })
        );
    }
  }

  private emptyCache() {
    this.cachedPages.clear();
    this.currentPageToken = undefined;
    this.nextPageToken = undefined;
  }

  protected updatePaginator(previousPageIndex: number) {
  }

  public getDisplayName(option: any): any { }

  protected abstract loadPageFunction(
    filterCriteria: string,
    pageSize: number,
    pageToken: string,
    orderBy: string): Observable<GetPageFunctionResponse<T>>;

  constructor(pageSize?: number) {
    super();
    if (pageSize) {
      this.pageSize = pageSize;
    } else {
      const fittingPageSize = Math.floor((document.body.offsetHeight - 300) / 40);
      this.pageSize = Math.max(NebulaDataSource.MIN_PAGESIZE, Math.min(NebulaDataSource.MAX_PAGESIZE, fittingPageSize));
    }
  }

}

export interface GetPageFunctionResponse<T> {
  pageElements: Array<T>;
  nextPageToken: string;
}
